import React, { forwardRef, HTMLAttributes, RefObject, useCallback, useRef, useState } from 'react';
import { styled } from '@mui/material';
import Image from './Image';
import Compressor from 'compressorjs';
import { showMessage } from '@/actions/common';
import dropzone from './Dropzone';
import t from '@/utils/translate';
import TrashIcon from './icons/Trash';
import UploadIcon from './icons/Upload';

type DropzoneAreaProps = HTMLAttributes<HTMLDivElement> & { sizeH?: number, sizeV?: number };

const DropzoneArea = dropzone<DropzoneAreaProps>(styled('div')<DropzoneAreaProps>(({ sizeH, sizeV }) => ({
  ...(sizeH && { width: sizeH }),
  ...(sizeV && { height: sizeV }),
  padding: '16px 18px',
  border: '1px solid #DADADA',
  borderRadius: '10px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '14px',
})));

const Title = styled('span')({
  height: '16px',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '16px',
  textAlign: 'center',
  color: '#42526E',
});

const Hint = styled('span')({
  fontWeight: 400,
  fontSize: '13px',
  lineHeight: '15px',
  textAlign: 'center',
  color: '#C1C7D0',
});

type PreviewBlockProps = {
  sizeH?: number,
  sizeV?: number,
  variant: 'default' | 'rounded'
};
const PreviewBlock = styled('div')(({ variant = 'default', sizeH, sizeV }: PreviewBlockProps) => ({
  position: 'relative',
  width: '100%',
  ...(sizeH && { width: sizeH }),
  ...(sizeV && { height: sizeV }),
  border: '1px solid #EBECF0',
  cursor: 'pointer',
  ...(variant === 'rounded'
    ? {
      borderRadius: '50%',
      overflow: 'hidden',
    }
    : {
      borderRadius: '8px',
    }
  ),

  '& button': { display: 'none' },
  '&:hover button': { display: 'block' }
}));

const DragIndicator = styled('div')(({ opaque = false }: { opaque?: boolean }) => ({
  color: '#FFFFFF',
  background: opaque ? '#6B778C' : '#344563B2',
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}));

const ClearButton = styled('button')(({ variant = 'default', hint }: { variant: 'default' | 'rounded', hint?: string }) => ({
  position: 'absolute',
  width: '44px',
  height: '44px',

  fontSize: '24px',
  padding: '10px',
  border: 'none',
  cursor: 'pointer',

  ...(variant === 'rounded'
    ? {
      bottom: '10px',
      right: 'calc(50% - 22px)',
      borderRadius: '50%',
      background: '#FFFFFF80',
      color: '#6B778C',
      '&:hover': {
        background: '#FFFFFFCC',
        color: '#DE350B',
      },
    }
    : {
      top: 0,
      right: 0,
      borderRadius: '6px',
      background: ' #FFFFFF80',
      color: '#DE350B66',
      '&:hover': {
        background: '#FFFFFFB2',
        color: '#DE350B',
      },
    }
  ),

  ...(hint ? {
    '&::after': {
      content: `'${hint}'`,
      padding: '6px 10px 6px 10px',
      borderRadius: '5px',
      border: '1px solid rgba(0, 0, 0, 0.17)',
      boxShadow: '0px 0px 6px 0px rgba(0, 0, 0, 0.07)',
      background: '#FFFFFF',
      fontSize: '14px',
      fontWeight: '400',
      lineHeight: '16px',
      letterSpacing: '0em',
      textAlign: 'left',
      color: '#000000',
      position: 'absolute',
      bottom: '31px',
      right: '44px',
      pointerEvents: 'none',
      opacity: 0,
      transition: 'all 0s 0s',
    },
    '&:hover::after': {
      opacity: 1,
      transition: 'all 200ms 500ms',
    }
  } : {}),

}));

const accepted = [
  'image/apng',
  'image/avif',
  'image/gif',
  'image/jpeg',
  'image/png',
  'image/svg+xml',
  'image/webp',
];
const acceptedFormatsString = accepted.map((f) => f.split('/')[1].toUpperCase()).join(', ');

export type ImageLoaderProps = {
  title?: string,
  source: string | null | false,
  onSet: (f: File) => void,
  onClear?: () => void,
  sizeH?: number,
  sizeV?: number,
  previewSizeH?: number,
  previewSizeV?: number,
  placeholder?: React.ReactElement,
  disabled?: boolean,
  opaque?: boolean,
  hint?: string,
  clearButtonHint?: string,
  clearable?: boolean,
  variant?: 'default' | 'rounded',
} & Omit<HTMLAttributes<HTMLDivElement>, 'onDrop' | 'onDragEnter' | 'onDragLeave' | 'disabled'>;

export default forwardRef((
  {
    source = null,
    onSet,
    onClear,
    sizeH,
    sizeV,
    previewSizeH,
    previewSizeV,
    title,
    placeholder,
    disabled = false,
    opaque = true,
    hint,
    clearButtonHint,
    clearable = true,
    variant = 'default',
    ...divProps
  }: ImageLoaderProps,
  ref: RefObject<HTMLDivElement>
) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const [ dragIndicator, setDragIndicator ] = useState<boolean>(false);

  const clickHandler = useCallback(() => {
    if (inputRef.current && !disabled) {
      inputRef.current.click();
    }
  }, [inputRef.current, disabled]);

  const onDropCallback = useCallback((files: File[]) => {
    setDragIndicator(false);
    if (!files.length) return;
    if (files[0]?.type === 'image/svg+xml') {
      onSet(files[0]);
      return;
    }

    new Compressor(files[0], {
      quality: 0.6,
      maxWidth: 512,
      maxHeight: 512,
      success: onSet,
      error(err: Error) {
        console.error(err);
        showMessage(t('image.error.processingFail'), 'error');
      },
    });
  }, [ onSet ]);

  const onFilteredCallback = useCallback((files: File[]) => {
    if (!files.length) return;
    showMessage(t('image.error.unsupportedFormat', { formats: acceptedFormatsString }), 'error');
  }, []);

  const onClearCallback = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    !!onClear && onClear();
  }, [ onClear ]);

  return (
    <>
      <DropzoneArea
        {...divProps}
        sizeH={sizeH}
        sizeV={sizeV}
        accept={accepted}
        onFileDrop={onDropCallback}
        onDragEnter={() => setDragIndicator(true)}
        onDragLeave={() => setDragIndicator(false)}
        onFilesFiltered={onFilteredCallback}
        disabled={disabled}
        noClick={true}
        inputRef={inputRef}
        ref={ref}
        multiple={false}
      >
        {!!title && (<Title>{title}</Title>)}
        <PreviewBlock
          onClick={clickHandler}
          variant={variant}
          sizeH={previewSizeH}
          sizeV={previewSizeV}
        >
          {!!source && clearable && onClear && (
            <ClearButton
              onClick={onClearCallback}
              hint={clearButtonHint || t('action.delete')}
              variant={variant}
            >
              <TrashIcon/>
            </ClearButton>
          )}
          {<Image
            source={source}
            sizeH={previewSizeH}
            sizeV={previewSizeV}
            placeholder={placeholder}
            sx={{
              borderRadius: '8px',
              ...(opaque ? {} : { opacity: '30%' })
            }}
          />}
          {dragIndicator && <DragIndicator opaque={!source}><UploadIcon sx={{ fontSize: '70px' }}/></DragIndicator>}
        </PreviewBlock>

        {(!disabled || hint) && (
          <Hint>
            {hint || t('image.hint.drag', true)}
          </Hint>
        )}
      </DropzoneArea>
    </>
  );
});